import { type Locale } from '@/lib/i18n';
import {
  BASE_URL,
  ORGANIZATION_ID,
  OG_IMAGE_URL,
  getRouteMeta,
} from '@/lib/metadata-helper';
import { buildOffer } from '@/lib/pricing-schema';

/**
 * Structured data for the commercial pages that had none.
 *
 * The audit flagged that `/pricing`, `/demo` and the customer stories carried
 * no page-specific schema. Those were fixed first, and the rest of the list
 * was not: twenty-nine product, channel, industry and helpdesk pages still
 * shipped nothing but the site-wide Organization and WebSite nodes — including
 * six of the seven `/helpdesks/*` pages, whose seventh (`/intercom`) has a
 * full treatment. Same template, same kind of query, no markup.
 *
 * Everything emitted here is resolved from `routeMetadata`, the same source
 * the page's own `<title>` and meta description come from. Nothing is
 * invented, and a route with no entry returns null rather than getting a
 * made-up name. Software pages carry the published rate by reference through
 * `buildOffer`, never as a restated number. There are deliberately still no
 * ratings: we have no review corpus to aggregate, and inventing one to satisfy
 * a validator is the kind of markup that gets rich results revoked.
 */

/** Type of the primary node, by what the page actually is. */
type PageKind = 'software' | 'collection' | 'page';

/**
 * Which routes get which node.
 *
 * Product, channel and helpdesk pages describe the software, so they get
 * `SoftwareApplication`. Index pages list other pages, so they get
 * `CollectionPage`. Industry pages are positioning for a segment rather than
 * a distinct product, so they get a plain `WebPage` — claiming a separate
 * SoftwareApplication per industry would assert six products that don't exist.
 */
const KIND_BY_ROUTE: Record<string, PageKind> = {
  '/agent5': 'software',
  '/workflows': 'software',
  '/companion': 'software',
  '/customer-insights': 'software',
  '/knowledge-gap-detection': 'software',
  '/app-builder': 'software',
  '/outbound-sequencing': 'software',
  '/open-telephony': 'software',
  '/sub-processes': 'software',
  '/whitelabel': 'software',

  '/channels': 'collection',
  '/channels/voice': 'software',
  '/channels/chat': 'software',
  '/channels/email': 'software',
  '/channels/whatsapp': 'software',
  '/channels/slack': 'software',
  '/channels/social': 'software',

  '/industries': 'collection',
  '/industries/healthcare': 'page',
  '/industries/financial-services': 'page',
  '/industries/mobility': 'page',
  '/industries/technology': 'page',
  '/industries/travel-hospitality': 'page',
  '/industries/tour-operators': 'page',

  '/helpdesks': 'collection',
  '/helpdesks/zendesk': 'software',
  '/helpdesks/salesforce': 'software',
  '/helpdesks/hubspot': 'software',
  '/helpdesks/freshdesk': 'software',
  '/helpdesks/twilio-flex': 'software',
  '/helpdesks/open': 'software',

  // Index pages whose own page.tsx is the schema host (see IndexRouteSchema)
  // — putting these in a layout would leak the node onto every child.
  '/compare': 'collection',
  '/customer-stories': 'collection',
  '/integrations': 'collection',
  '/templates': 'collection',

  // Leaf pages. `/news/...` gets a WebPage rather than an Article: the page
  // renders no date, and an Article with an invented `datePublished` is the
  // problem this work exists to remove.
  '/guarantee': 'page',
  '/launches': 'page',
  '/cheatsheet': 'page',
  '/terms': 'page',
  '/privacy-policy': 'page',
  '/cookie-policy': 'page',
  '/gdpr': 'page',
  '/incentive-terms': 'page',
  '/legal/standard-terms': 'page',
  '/legal/price-list': 'page',
  '/legal/dpa-standard-terms': 'page',
  '/legal/dpa-annexes': 'page',
  '/legal/sla': 'page',
  '/news/announcing-our-new-funding-round': 'page',
};

/**
 * The `/compare/<vertical>` hubs are a dynamic route, so they cannot be listed
 * individually. They are index pages over their vertical's comparisons, which
 * makes them CollectionPages like any other index.
 */
const COMPARE_VERTICAL = /^\/compare\/[a-z0-9-]+$/;

function kindFor(route: string): PageKind | undefined {
  return (
    KIND_BY_ROUTE[route] ??
    (COMPARE_VERTICAL.test(route) ? 'collection' : undefined)
  );
}

export function hasRouteSchema(route: string): boolean {
  return kindFor(route) !== undefined;
}

function absolute(route: string, isAr: boolean): string {
  return isAr ? `${BASE_URL}/ar${route}` : `${BASE_URL}${route}`;
}

/**
 * Build the trail from the route's own segments, naming each ancestor with
 * the title that ancestor's page actually uses. A segment with no
 * `routeMetadata` entry is skipped rather than guessed at.
 */
function breadcrumbs(
  route: string,
  locale: Locale,
  isAr: boolean,
  leaf?: string,
) {
  const items: Array<{ name: string; item: string }> = [
    {
      name: isAr ? 'الرئيسية' : 'Home',
      item: isAr ? `${BASE_URL}/ar` : BASE_URL,
    },
  ];

  const segments = route.split('/').filter(Boolean);
  let path = '';
  for (const segment of segments) {
    path += `/${segment}`;
    const isLeaf = path === route;
    // A page that builds its own title inline can pass it as `leaf`, so the
    // trail ends with the heading the reader actually sees rather than with a
    // `routeMetadata` entry the page stopped using. /tools/roi-calculator
    // renders "AI Customer Support ROI Calculator"; routeMetadata still says
    // "AI ROI Calculator".
    if (isLeaf && leaf) {
      items.push({ name: leaf, item: absolute(path, isAr) });
      continue;
    }
    const meta = getRouteMeta(path, locale);
    if (!meta) continue;
    items.push({ name: meta.title, item: absolute(path, isAr) });
  }

  return {
    '@type': 'BreadcrumbList',
    '@id': `${absolute(route, isAr)}#breadcrumb`,
    itemListElement: items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
}

/**
 * Returns the `@graph` for a route, or null when the route has no recorded
 * kind or no metadata to describe it with.
 */
export function buildRouteSchema(
  route: string,
  locale: string,
  /**
   * Name and description for routes that build their own title inline rather
   * than through `routeMetadata` (/compare, /guarantee). They pass the strings
   * they already computed, so the schema quotes the page's real title instead
   * of a second copy that could drift from it.
   */
  override?: { name: string; description: string },
): Record<string, unknown> | null {
  const kind = kindFor(route);
  if (!kind) return null;

  const loc = locale as Locale;
  const meta = override
    ? { title: override.name, description: override.description }
    : getRouteMeta(route, loc);
  if (!meta) return null;

  const isAr = loc === 'ar';
  const url = absolute(route, isAr);
  const lang = isAr ? 'ar' : 'en';

  const primary: Record<string, unknown> = {
    '@id': `${url}#page`,
    name: meta.title,
    description: meta.description,
    url,
    inLanguage: lang,
    image: OG_IMAGE_URL,
  };

  if (kind === 'software') {
    Object.assign(primary, {
      '@type': 'SoftwareApplication',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      provider: { '@id': ORGANIZATION_ID },
      // The rate still lives in exactly one place. `buildOffer` reads the same
      // `PRICING_BY_LOCALE` entry /pricing renders, so quoting it here adds a
      // reference, not a second copy to drift — which is what made four
      // different prices possible the last time this was duplicated by hand.
      offers: buildOffer(loc),
    });
  } else {
    Object.assign(primary, {
      '@type': kind === 'collection' ? 'CollectionPage' : 'WebPage',
      isPartOf: { '@id': `${BASE_URL}/#website` },
      about: { '@id': ORGANIZATION_ID },
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [primary, breadcrumbs(route, loc, isAr)],
  };
}

/**
 * The `BreadcrumbList` node on its own, for a page that already emits its own
 * primary node inline and only lacks the trail. The tools pages and the course
 * template each build a bespoke `SoftwareApplication` or `Course`; they were
 * among the 66 pages shipping no breadcrumb at all.
 *
 * `leaf` names the last crumb when the page's own title differs from its
 * `routeMetadata` entry.
 */
export function buildBreadcrumbSchema(
  route: string,
  locale: string,
  leaf?: string,
): Record<string, unknown> {
  const loc = (locale === 'ar' ? 'ar' : 'en') as Locale;
  return {
    '@context': 'https://schema.org',
    ...breadcrumbs(route, loc, loc === 'ar', leaf),
  };
}
